import type { DocumentData, FirestoreError, QuerySnapshot } from 'firebase/firestore';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type { DocumentCategories, SupportDoc } from '../../components/TenderDetails/types';
import { firestore } from '../../services/firebase';
import type { LibraryFileMetadata } from '../../services/libraryService';
import type { User } from '../../types/auth';
import type { Tender } from '../../types/tender';

export interface UseSupportDocsSubscriptionsProps {
  tenderId: string | undefined;
  tender: Tender | null;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  isAuthLoading: boolean;
  isTenderLoaded: boolean;
  hasPermission: (permission: string) => boolean;
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
}

export const useSupportDocsSubscriptions = ({
  tenderId,
  tender,
  account,
  isAuthLoading,
  isTenderLoaded,
  hasPermission,
  showSnackbar,
}: UseSupportDocsSubscriptionsProps) => {
  const [user_docs, setUser_docs] = useState<SupportDoc[]>([]);
  const [tenderForms, setTenderForms] = useState<SupportDoc[]>([]);
  const [tenderCVs, setTenderCVs] = useState<SupportDoc[]>([]);
  const [activeLibraryFiles, setActiveLibraryFiles] = useState<LibraryFileMetadata[]>([]);
  const [documentCategories, setDocumentCategories] = useState<DocumentCategories>({});

  useEffect(() => {
    if (
      isAuthLoading ||
      !account?.uid ||
      !tenderId ||
      !account?.clientId ||
      !isTenderLoaded ||
      !tender
    ) {
      setUser_docs([]);
      setTenderForms([]);
      setTenderCVs([]);
      return undefined;
    }

    const isOwner = tender.ownerId === account.uid;
    const userTenderPermissions = tender.memberPermissions?.[account.uid];
    const checkTenderPerm = (perm: string) => {
      if (userTenderPermissions === undefined) {
        return hasPermission(perm);
      }
      if (Array.isArray(userTenderPermissions)) {
        return userTenderPermissions.includes(perm);
      }
      if (typeof userTenderPermissions === 'object' && userTenderPermissions !== null) {
        return !!(userTenderPermissions as Record<string, boolean>)[perm];
      }
      return false;
    };
    const hasFullAccess = Boolean(isOwner || checkTenderPerm('tender_full_access'));

    const hasViewAllFiles = Boolean(
      hasFullAccess || checkTenderPerm('tender_files_view_client') || hasPermission('system_manage')
    );

    const hasCVView = Boolean(
      hasFullAccess || checkTenderPerm('library_view_client') || hasPermission('system_manage')
    );

    let libraryQuery: import('firebase/firestore').Query = collection(
      firestore,
      `tenders/${tenderId}/library`
    );
    let tenderDocsQuery: import('firebase/firestore').Query = collection(
      firestore,
      `tenders/${tenderId}/tender_docs`
    );

    if (!hasViewAllFiles) {
      libraryQuery = query(libraryQuery, where('uploaderId', '==', account.uid));
      tenderDocsQuery = query(tenderDocsQuery, where('uploaderId', '==', account.uid));
    }

    const tenderDocsUnsubscribe = onSnapshot(
      tenderDocsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const detectedFormsList = tender?.detectedForms || [];
        const detectedFormLinks = tender?.detectedFormLinks || {};
        const formsData: SupportDoc[] = [];

        for (const formName of detectedFormsList) {
          const pathParts = formName.split('/');
          const fileNameOnly = pathParts[pathParts.length - 1] || formName;
          const parts = fileNameOnly.split('.');
          const baseName = parts.length > 1 ? parts.slice(0, -1).join('.') : fileNameOnly;

          // Find the matching tracking document in tender_docs
          const trackingDocSnap = snapshot.docs.find((d) => d.id === baseName);
          const trackingDoc = trackingDocSnap ? trackingDocSnap.data() : null;

          formsData.push({
            id: baseName,
            originalFilename: formName,
            doc_type: 'strict_form',
            isStrictForm: true,
            downloadUrl: detectedFormLinks[formName] || undefined,
            status: 'ready',
            generationStatus: trackingDoc?.generationStatus || 'idle',
            generationProgress: trackingDoc?.generationProgress,
            generationError: trackingDoc?.generationError,
            filledVersionStorageUri: trackingDoc?.filledVersionStorageUri,
            filledAt: trackingDoc?.filledAt,
            uploadedAt:
              trackingDoc?.uploadedAt ||
              tender?.createdAt ||
              tender?.updatedAt ||
              new Date().toISOString(),
            startedAt:
              trackingDoc?.startedAt ||
              tender?.createdAt ||
              tender?.updatedAt ||
              new Date().toISOString(),
          } as SupportDoc);
        }

        // We only want to update the strict forms in tenderForms that originated from detectedForms
        setTenderForms((prev) => {
          const nonDetectedForms = prev.filter(
            (p) => !p.originalFilename || !detectedFormsList.includes(p.originalFilename)
          );
          const updatedDetectedForms = formsData.map((newDoc) => {
            const oldDoc = prev.find((p) => p.originalFilename === newDoc.originalFilename);

            if (
              oldDoc?.generationStatus === 'processing' &&
              (newDoc.generationStatus === 'success' || newDoc.generationStatus === 'error')
            ) {
              return newDoc;
            }

            if (
              (oldDoc?.generationStatus === 'processing' ||
                oldDoc?.generationStatus === 'initializing') &&
              newDoc.generationStatus !== 'success' &&
              newDoc.generationStatus !== 'error'
            ) {
              return {
                ...newDoc,
                generationStatus: oldDoc.generationStatus,
                generationProgress: newDoc.generationProgress ?? oldDoc.generationProgress,
                ...(oldDoc.startedAt ? { startedAt: oldDoc.startedAt } : {}),
              } as SupportDoc;
            }

            return newDoc;
          });
          return [...nonDetectedForms, ...updatedDetectedForms];
        });
      },
      (error: FirestoreError) => {
        if (error.code !== 'permission-denied') {
          // biome-ignore lint/suspicious/noConsole: Error logging
          console.error('Firebase snapshot error on tender_docs:', error);
        }
      }
    );

    const libraryUnsubscribe = onSnapshot(
      libraryQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const userDocsData: SupportDoc[] = [];
        const formsData: SupportDoc[] = [];
        const cvsData: SupportDoc[] = [];

        for (const docSnap of snapshot.docs) {
          const docData = { id: docSnap.id, ...docSnap.data() } as SupportDoc;
          if (docData.doc_type === 'form' || docData.doc_type === 'strict_form') {
            if (docData.doc_type === 'strict_form') {
              docData.isStrictForm = true;
            } else {
              docData.isStrictForm = false;
            }
            formsData.push(docData);
          } else {
            if (
              docData.doc_type === 'cv' ||
              docData.doc_type === 'cvs' ||
              docData.doc_type === 'people_cv' ||
              docData.doc_type?.toLowerCase().includes('cv')
            ) {
              if (hasCVView) {
                cvsData.push(docData);
              }
            }
            userDocsData.push(docData);
          }
        }

        setUser_docs(userDocsData);

        // Update non-detected forms (forms uploaded manually to library)
        setTenderForms((prev) => {
          const detectedFormsList = tender?.detectedForms || [];
          const detectedFormsInPrev = prev.filter(
            (p) => p.originalFilename && detectedFormsList.includes(p.originalFilename)
          );
          const updatedLibraryForms = formsData.map((newDoc) => {
            const oldDoc = prev.find((p) => p.originalFilename === newDoc.originalFilename);

            if (
              oldDoc?.generationStatus === 'processing' &&
              (newDoc.generationStatus === 'success' || newDoc.generationStatus === 'error')
            ) {
              return newDoc;
            }

            if (
              (oldDoc?.generationStatus === 'processing' ||
                oldDoc?.generationStatus === 'initializing') &&
              newDoc.generationStatus !== 'success' &&
              newDoc.generationStatus !== 'error'
            ) {
              return {
                ...newDoc,
                generationStatus: oldDoc.generationStatus,
                generationProgress: newDoc.generationProgress ?? oldDoc.generationProgress,
                ...(oldDoc.startedAt ? { startedAt: oldDoc.startedAt } : {}),
              } as SupportDoc;
            }

            return newDoc;
          });
          return [...detectedFormsInPrev, ...updatedLibraryForms];
        });

        setTenderCVs((prev) =>
          cvsData.map((newDoc) => {
            const oldDoc = prev.find(
              (p) => (p.id && p.id === newDoc.id) || p.originalFilename === newDoc.originalFilename
            );
            if (
              oldDoc?.startedAt &&
              (oldDoc.generationStatus === 'initializing' ||
                oldDoc.generationStatus === 'processing') &&
              (newDoc.generationStatus === 'initializing' ||
                newDoc.generationStatus === 'processing') &&
              !newDoc.startedAt
            ) {
              return { ...newDoc, startedAt: oldDoc.startedAt };
            }
            return newDoc;
          })
        );
      },
      (error: FirestoreError) => {
        if (error.code === 'permission-denied') {
          showSnackbar('Permission denied loading documents.', 'error');
        } else {
          // biome-ignore lint/suspicious/noConsole: Error logging
          console.error('Firebase snapshot error on library:', error);
        }
      }
    );

    return () => {
      libraryUnsubscribe();
      tenderDocsUnsubscribe();
    };
  }, [
    tenderId,
    account?.uid,
    account?.clientId,
    isTenderLoaded,
    isAuthLoading,
    hasPermission,
    showSnackbar,
    tender?.ownerId,
    tender?.memberPermissions,
    tender,
  ]);

  return {
    user_docs,
    setUser_docs,
    tenderForms,
    setTenderForms,
    tenderCVs,
    setTenderCVs,
    activeLibraryFiles,
    setActiveLibraryFiles,
    documentCategories,
    setDocumentCategories,
  };
};
